import { create } from 'zustand';
import apiClient from './api/apiClient';

const isFetchOptions = (value) =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).every((key) => key === 'force');

const splitFetchArgs = (args) => {
  if (args.length === 0) return { callArgs: [], force: false };
  const last = args[args.length - 1];
  if (isFetchOptions(last)) {
    return { callArgs: args.slice(0, -1), force: !!last.force };
  }
  return { callArgs: args, force: false };
};

/**
 * Factory for standard admin CRUD Zustand stores.
 *
 * Optional `cache: { ttl }` on list fetches:
 * - omit / null → always network
 * - `{ ttl: number | Infinity }` → skip network when cache key matches and TTL valid
 * - pass `{ force: true }` as the last fetch arg to bypass cache (dedicated pages)
 * Mutations clear the list cache.
 */
export function createResourceStore({
  endpoint,
  collectionKey,
  itemKey,
  paginated = false,
  totalKey,
  listPath,
  createPath,
  updatePath,
  deletePath,
  listResponseKey,
  mapListParams,
  enrichUpdate,
  hasCreate = true,
  hasUpdate = true,
  hasDelete = true,
  hasFetchById = false,
  methods = {},
  extraState = {},
  itemStateKey,
  cache = null,
  extend,
}) {
  const {
    fetch: fetchName = 'fetchList',
    create: createName = 'createItem',
    update: updateName = 'updateItem',
    delete: deleteName = 'deleteItem',
    fetchById: fetchByIdName = 'fetchById',
  } = methods;

  const resolveListPath = () =>
    typeof listPath === 'function' ? listPath() : listPath || endpoint;
  const resolveCreatePath = () =>
    typeof createPath === 'function' ? createPath() : createPath || endpoint;
  const resolveUpdatePath = (id) =>
    updatePath ? updatePath(id) : `${endpoint}/${id}`;
  const resolveDeletePath = (id) =>
    deletePath ? deletePath(id) : `${endpoint}/${id}`;
  const responseListKey = listResponseKey || collectionKey;
  const resolvedTotalKey =
    totalKey || `total${collectionKey.charAt(0).toUpperCase()}${collectionKey.slice(1)}`;
  const resolvedItemStateKey = itemStateKey || itemKey;
  const cacheEnabled = cache && cache.ttl != null;

  return create((set, get) => {
    const initial = {
      [collectionKey]: [],
      ...(paginated
        ? {
            [resolvedTotalKey]: 0,
            totalPages: 0,
          }
        : {}),
      _listCache: null,
      ...extraState,
    };

    const clearListCache = () => set({ _listCache: null });

    const isCacheFresh = (cacheKey) => {
      if (!cacheEnabled) return false;
      const entry = get()._listCache;
      if (!entry || entry.key !== cacheKey) return false;
      if (cache.ttl === Infinity) return true;
      return Date.now() - entry.fetchedAt < cache.ttl;
    };

    const actions = {
      [fetchName]: async (...args) => {
        try {
          const { callArgs, force } = splitFetchArgs(args);
          const params = mapListParams
            ? mapListParams(...callArgs)
            : paginated
              ? {
                  ...(callArgs[0] != null && callArgs[0] !== '' ? { page: callArgs[0] } : {}),
                  search: callArgs[1] ?? '',
                }
              : {};
          const cacheKey = JSON.stringify(params ?? {});

          if (!force && isCacheFresh(cacheKey)) {
            return get()[collectionKey];
          }

          const data = await apiClient.get(resolveListPath(), params);
          if (paginated) {
            set({
              [collectionKey]: data[responseListKey],
              [resolvedTotalKey]: data[resolvedTotalKey],
              totalPages: data.totalPages,
              _listCache: cacheEnabled ? { key: cacheKey, fetchedAt: Date.now() } : null,
            });
          } else {
            set({
              [collectionKey]: data[responseListKey],
              _listCache: cacheEnabled ? { key: cacheKey, fetchedAt: Date.now() } : null,
            });
          }
          return data[responseListKey];
        } catch (error) {
          console.error(`Failed to fetch ${collectionKey}:`, error);
        }
      },

      ...(hasFetchById
        ? {
            [fetchByIdName]: async (id) => {
              try {
                const data = await apiClient.get(`${endpoint}/${id}`);
                const item = data[itemKey];
                set((state) => ({
                  [resolvedItemStateKey]: item,
                  [collectionKey]: (state[collectionKey] || []).map((row) =>
                    row.id === id ? item : row
                  ),
                }));
                return item;
              } catch (error) {
                console.error(`Failed to fetch ${itemKey} ${id}:`, error);
                throw error;
              }
            },
          }
        : {}),

      ...(hasCreate
        ? {
            [createName]: async (payload) => {
              try {
                const data = await apiClient.post(resolveCreatePath(), payload);
                const item = data[itemKey];
                set((state) => ({
                  [collectionKey]: [...state[collectionKey], item],
                  _listCache: null,
                }));
                return item;
              } catch (error) {
                console.error(`Failed to create ${itemKey}:`, error);
                throw error;
              }
            },
          }
        : {}),

      ...(hasUpdate
        ? {
            [updateName]: async (id, updatedData) => {
              try {
                const body = enrichUpdate ? enrichUpdate(updatedData) : updatedData;
                const data = await apiClient.patch(resolveUpdatePath(id), body);
                const item = data[itemKey];
                set((state) => ({
                  [collectionKey]: state[collectionKey].map((row) =>
                    row.id === id ? item : row
                  ),
                  _listCache: null,
                }));
                return item;
              } catch (error) {
                console.error(`Failed to update ${itemKey}:`, error);
                throw error;
              }
            },
          }
        : {}),

      ...(hasDelete
        ? {
            [deleteName]: async (id) => {
              try {
                const data = await apiClient.delete(resolveDeletePath(id));
                set((state) => ({
                  [collectionKey]: state[collectionKey].filter((row) => row.id !== id),
                  _listCache: null,
                }));
                return data.message;
              } catch (error) {
                console.error(`Failed to delete ${itemKey}:`, error);
                throw error;
              }
            },
          }
        : {}),

      clearListCache,
    };

    const extra = extend ? extend(set, get, { apiClient, ...actions }) : {};

    return {
      ...initial,
      ...actions,
      ...extra,
    };
  });
}

export default createResourceStore;
